// src/App.jsx
import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import ThemeToggle from "./components/ThemeToggle/ThemeToggle";
import StudyReminders from "./components/StudyReminders/StudyReminders";
import "./App.css";
import "./darkmode.css";
import Landing from "./pages/Landing/Landing";
import Home from "./pages/Home/Home";
import About from "./pages/About/About";
import Contact from "./pages/Contact/Contact";
import Register from "./pages/Register/Register";
import Login from "./pages/Login/Login";
import VerifyEmail from "./pages/Verify/VerifyEmail";
import TeacherDashboard from "./pages/TeacherDashboard/TeacherDashboard";
import StudentDashboard from "./pages/StudentDashboard/StudentDashboard";
import AdminDashboard from "./pages/AdminDashboard/AdminDashboard";
import Profile from "./pages/Profile/Profile";
import ProfileEdit from "./pages/ProfileEdit/ProfileEdit";
import AgentHub from "./pages/AgentHub/AgentHub";
import AgentList from "./pages/AgentHub/AgentList";
import AgentInteraction from "./pages/AgentInteraction/AgentInteraction";
import CurriculumViewer from "./pages/Agents/CurriculumViewer";
import QuizAgent from "./pages/Agents/QuizAgent";
import TutorAgent from "./pages/Agents/TutorAgent";
import AnalyticsAgent from "./pages/Agents/AnalyticsAgent";
import FeedbackAgent from "./pages/Agents/FeedbackAgent";
import ProgressMonitor from "./pages/Agents/ProgressMonitor";
import RecommendationAgent from "./pages/Agents/RecommendationAgent";
import SummarizingAgent from "./pages/Agents/SummarizingAgent";
import CourseCatalog from "./pages/Courses/CourseCatalog";
import CourseSubject from "./pages/Courses/CourseSubject";
import CourseLesson from "./pages/Courses/CourseLesson";
import Subscribe from "./pages/Billing/Subscribe";
import BillingCallback from "./pages/Billing/BillingCallback";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
// Teacher Agent Components
import TeacherSupportAgent from "./pages/TeacherAgents/TeacherSupportAgent";
import AnalyticsTeacherAgent from "./pages/TeacherAgents/AnalyticsTeacherAgent";
import StudentActivity from "./pages/TeacherAgents/StudentActivity";
import FeedbackTeacherAgent from "./pages/TeacherAgents/FeedbackTeacherAgent";
import LessonPlannerAgent from "./pages/TeacherAgents/LessonPlannerAgentSimple";
import LessonPlanGenerator from "./pages/TeacherAgents/LessonPlanGenerator";
import CurriculumViewerAgent from "./pages/TeacherAgents/CurriculumViewerAgent";
import TeacherTutorAgent from "./pages/TeacherAgents/TeacherTutorAgent";

function App() {
  return (
    <ThemeProvider>
      <Router>
        <ThemeToggle />
        <StudyReminders />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Authenticated Routes */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />

          {/* Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <StudentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-dashboard"
            element={
              <ProtectedRoute userType="student">
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected Teacher Routes */}
          <Route
            path="/teacher-dashboard"
            element={
              <ProtectedRoute userType="teacher">
                <TeacherDashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected Admin Route */}
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute userType="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected Common Routes */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/profile/edit"
            element={
              <ProtectedRoute userType="student">
                <ProfileEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/profile/edit"
            element={
              <ProtectedRoute userType="teacher">
                <ProfileEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents"
            element={
              <ProtectedRoute>
                <AgentHub />
              </ProtectedRoute>
            }
          />

          {/* Courses */}
          <Route path="/courses" element={<ProtectedRoute><CourseCatalog /></ProtectedRoute>} />
          <Route path="/courses/:slug" element={<ProtectedRoute><CourseSubject /></ProtectedRoute>} />
          <Route path="/courses/:slug/:chapterId" element={<ProtectedRoute><CourseLesson /></ProtectedRoute>} />

          {/* Subscription (Paystack) */}
          <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />
          <Route path="/billing/callback" element={<ProtectedRoute><BillingCallback /></ProtectedRoute>} />

          {/* Agent Routes */}
          <Route
            path="/agents/curriculum-viewer"
            element={
              <ProtectedRoute>
                <CurriculumViewer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents/quiz"
            element={
              <ProtectedRoute>
                <QuizAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents/tutor"
            element={
              <ProtectedRoute>
                <TutorAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents/feedback"
            element={
              <ProtectedRoute>
                <FeedbackAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents/progress"
            element={
              <ProtectedRoute>
                <ProgressMonitor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents/recommendations"
            element={
              <ProtectedRoute>
                <RecommendationAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents/summarize"
            element={
              <ProtectedRoute>
                <SummarizingAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agents/explain"
            element={
              <ProtectedRoute>
                <SummarizingAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/:agentType"
            element={
              <ProtectedRoute>
                <AgentInteraction />
              </ProtectedRoute>
            }
          />

          {/* Teacher Agent Routes */}
          <Route
            path="/teacher/agents/support"
            element={
              <ProtectedRoute userType="teacher">
                <TeacherSupportAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/agents/tutor"
            element={
              <ProtectedRoute userType="teacher">
                <TeacherTutorAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/student-activity"
            element={
              <ProtectedRoute userType="teacher">
                <StudentActivity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/student-activity/:studentId"
            element={
              <ProtectedRoute userType="teacher">
                <StudentActivity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/agents/analytics"
            element={
              <ProtectedRoute userType="teacher">
                <AnalyticsTeacherAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/agents/feedback"
            element={
              <ProtectedRoute userType="teacher">
                <FeedbackTeacherAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/agents/lesson-planner"
            element={
              <ProtectedRoute userType="teacher">
                <LessonPlannerAgent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/agents/lesson-generator"
            element={
              <ProtectedRoute userType="teacher">
                <LessonPlanGenerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/agents/curriculum-viewer"
            element={
              <ProtectedRoute userType="teacher">
                <CurriculumViewerAgent />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
